// MaxRects bin packer (Best Short Side Fit).
//
// Replaces the old best-fit-by-height guillotine shelf packer. MaxRects keeps a
// list of *maximal* free rectangles (which may overlap) instead of a disjoint
// partition, so leftover space stays contiguous and later rects find placements
// the shelf packer missed. See MINI-25.
//
// Two entry points:
//   pack(rects, freeAreas)       — places fixed-size rects (mutates x/y).
//   packGroups(items, freeAreas) — places mini-groups that can *wrap* into a
//     grid, choosing the column count from the free rectangle each group lands
//     in rather than the whole sheet (MINI-26). Only the actually-filled cells
//     are marked used, so the unused tail of a partial last row is handed back
//     to the packer for the next group.
//
// `pack` mutates its rects in place and returns true iff everything fit.
//
// `packGroups` never fails destructively: a group that won't fit the page is
// *not* left with stale coordinates on top of another group (which made the
// sheet unreadable and hard to correct). Instead it flows into a spill band just
// below the page, keeping a valid, non-overlapping rectangle so the user can
// still see and shrink the offending group. It returns { didFit, overflow }
// where `overflow` is the groups that landed in the spill band, each marked with
// `overflow: true` (fitted groups get `overflow: false`).

function fitsIn(r, area) {
    return (r.width <= area.width && r.height <= area.height);
}

// Do the placed rect and a free rect overlap on a positive area?
function intersects(used, free) {
    return !(used.x >= free.x + free.width ||
             used.x + used.width <= free.x ||
             used.y >= free.y + free.height ||
             used.y + used.height <= free.y);
}

// Is rect `a` fully contained within rect `b`?
function contains(b, a) {
    return a.x >= b.x && a.y >= b.y &&
           a.x + a.width <= b.x + b.width &&
           a.y + a.height <= b.y + b.height;
}

// Drop degenerate rects and any free rect fully inside another. Without this the
// max-rects list grows unbounded and fragments; pruning keeps it to the useful
// maximal rectangles.
function prune(freeAreas) {
    const kept = freeAreas.filter(f => f.width > 0 && f.height > 0);
    return kept.filter((a, i) =>
        !kept.some((b, j) => j !== i && contains(b, a) &&
            // On exact duplicates keep only the first to avoid mutual removal.
            (b.width * b.height > a.width * a.height || j < i)));
}

// Pick the free area with the Best Short Side Fit: minimise the smaller of the
// two leftover gaps, tie-broken by the larger gap (long side fit).
function findFreeArea(rect, freeAreas) {
    let best = null;
    let bestShort = Infinity;
    let bestLong = Infinity;
    for (const free of freeAreas) {
        if (!fitsIn(rect, free)) continue;
        const leftoverW = free.width - rect.width;
        const leftoverH = free.height - rect.height;
        const shortFit = Math.min(leftoverW, leftoverH);
        const longFit = Math.max(leftoverW, leftoverH);
        if (shortFit < bestShort || (shortFit === bestShort && longFit < bestLong)) {
            best = free;
            bestShort = shortFit;
            bestLong = longFit;
        }
    }
    return best;
}

// Subtract `used` from every overlapping free rect, replacing it with the (up to
// four) maximal strips of it that remain uncovered. A zero-area `used` is a
// no-op. Returns a fresh, pruned array.
function subtractRect(freeAreas, used) {
    if (used.width <= 0 || used.height <= 0) return prune(freeAreas.slice());
    const result = [];
    for (const free of freeAreas) {
        if (!intersects(used, free)) {
            result.push(free);
            continue;
        }
        if (used.x > free.x) { // strip to the left
            result.push({ x: free.x, y: free.y, width: used.x - free.x, height: free.height });
        }
        if (used.x + used.width < free.x + free.width) { // strip to the right
            result.push({ x: used.x + used.width, y: free.y, width: (free.x + free.width) - (used.x + used.width), height: free.height });
        }
        if (used.y > free.y) { // strip above
            result.push({ x: free.x, y: free.y, width: free.width, height: used.y - free.y });
        }
        if (used.y + used.height < free.y + free.height) { // strip below
            result.push({ x: free.x, y: used.y + used.height, width: free.width, height: (free.y + free.height) - (used.y + used.height) });
        }
    }
    return prune(result);
}

// Replace the caller's free list in place with `next`, keeping the reference the
// caller holds valid.
function replaceFreeAreas(freeAreas, next) {
    freeAreas.length = 0;
    freeAreas.push(...next);
}

function pack(rects, freeAreas) {
    rects.sort((a, b) => b.height - a.height); // largest height rect first
    for (let i = 0; i < rects.length; i++) {
        const currRect = rects[i];
        const free = findFreeArea(currRect, freeAreas);
        if (!free) return false;

        currRect.x = free.x;
        currRect.y = free.y;

        const placed = { x: currRect.x, y: currRect.y, width: currRect.width, height: currRect.height };
        replaceFreeAreas(freeAreas, subtractRect(freeAreas, placed));
    }
    return true;
}

// Compare two candidate fits by Best Short Side Fit, tie-broken by long side fit.
// Returns true if `a` is strictly better than `b` (or `b` is null).
function isBetterFit(a, b) {
    if (b === null) return true;
    if (a.shortFit !== b.shortFit) return a.shortFit < b.shortFit;
    return a.longFit < b.longFit;
}

// Arrange a group of `item.count` unit cells (cellWidth × cellHeight) inside a
// free area of (freeW × freeH). Fill the row up to the free area's width, then
// wrap the overflow onto new rows — so a group stays a single row when it fits,
// and never grows wider than the space it lands in. Returns the arrangement, or
// null if it can't fit this area (one cell too wide, or too tall once wrapped).
function arrangeInArea(item, freeW, freeH) {
    const { count, cellWidth, cellHeight } = item;
    const maxColumns = Math.floor(freeW / cellWidth);
    if (maxColumns < 1) return null; // one cell is wider than this free area
    const columns = Math.min(count, maxColumns);
    const rows = Math.ceil(count / columns);
    const width = columns * cellWidth;
    const height = rows * cellHeight;
    if (height > freeH) return null; // too tall for this free area even wrapped
    const leftoverW = freeW - width;
    const leftoverH = freeH - height;
    return {
        columns, rows, width, height,
        shortFit: Math.min(leftoverW, leftoverH),
        longFit: Math.max(leftoverW, leftoverH),
    };
}

// Lay a group out at the given width, ignoring height. Used for overflow groups
// in the spill band, where there is always room below — so unlike arrangeInArea
// this never returns null (it falls back to a single column for a group whose
// one cell is wider than the band).
function arrangeSpill(item, width) {
    const maxColumns = Math.max(1, Math.floor(width / item.cellWidth));
    const columns = Math.min(item.count, maxColumns);
    const rows = Math.ceil(item.count / columns);
    return { columns, rows, width: columns * item.cellWidth, height: rows * item.cellHeight };
}

// Height of the labelled strip between the page and the first row of spilled
// groups. The canvas draws its "doesn't fit" banner here, so leave it clear.
// Exported so SheetCanvas positions the banner on the same line.
export const SPILL_HEADER = 0.5; // inches

// Place the groups that couldn't fit the page into a band starting just below
// it, flowing left-to-right and wrapping to a new row at the page width. Every
// group keeps a real, non-overlapping rectangle so nothing is ever drawn on top
// of anything else — the whole point of failing gracefully.
function spillOverflow(overflow, binRight, binBottom) {
    let cursorX = 0;
    let rowY = binBottom + SPILL_HEADER;
    let rowHeight = 0;
    for (const item of overflow) {
        const arr = arrangeSpill(item, binRight);
        if (cursorX > 0 && cursorX + arr.width > binRight) { // wrap to next row
            rowY += rowHeight;
            cursorX = 0;
            rowHeight = 0;
        }
        item.overflow = true;
        item.x = cursorX;
        item.y = rowY;
        item.columns = arr.columns;
        item.rows = arr.rows;
        item.width = arr.width;
        item.height = arr.height;
        cursorX += arr.width;
        rowHeight = Math.max(rowHeight, arr.height);
    }
}

// Mark a placed group's filled cells as used. The group fills full rows plus a
// (possibly partial) last row, so subtract that L-shape rather than the bounding
// box — the unused tail of the last row stays free for the next group.
function occupyGroup(freeAreas, item) {
    const filledLastRow = item.count - (item.rows - 1) * item.columns;
    const fullRows = {
        x: item.x, y: item.y,
        width: item.width, height: (item.rows - 1) * item.cellHeight,
    };
    const lastRow = {
        x: item.x, y: item.y + (item.rows - 1) * item.cellHeight,
        width: filledLastRow * item.cellWidth, height: item.cellHeight,
    };
    let next = subtractRect(freeAreas, fullRows);
    next = subtractRect(next, lastRow);
    replaceFreeAreas(freeAreas, next);
}

function packGroups(items, freeAreas) {
    // Bounds of the page, captured before packing consumes the free list. Overflow
    // groups spill into the band just below `binBottom`, across `binRight`.
    const binRight = freeAreas.reduce((m, a) => Math.max(m, a.x + a.width), 0);
    const binBottom = freeAreas.reduce((m, a) => Math.max(m, a.y + a.height), 0);

    // Tallest cell first, then by total area. A tall creature (e.g. a big single
    // mini) must reserve its full-height column *before* short groups fill the
    // width, or it gets stranded with nowhere to go — regardless of the order the
    // user added things. Sorting by area alone breaks this: a many-mini group can
    // out-weigh a single tall creature and grab the width first.
    items.sort((a, b) =>
        (b.cellHeight - a.cellHeight) ||
        (b.count * b.cellWidth * b.cellHeight - a.count * a.cellWidth * a.cellHeight));

    const overflow = [];
    for (const item of items) {
        let bestArr = null;
        let bestFree = null;
        for (const free of freeAreas) {
            const arr = arrangeInArea(item, free.width, free.height);
            if (arr && isBetterFit(arr, bestArr)) {
                bestArr = arr;
                bestFree = free;
            }
        }
        if (!bestArr) {
            // Won't fit the page. Defer to the spill band rather than aborting, so
            // this group and every group after it still get valid coordinates.
            overflow.push(item);
            continue;
        }

        item.overflow = false;
        item.x = bestFree.x;
        item.y = bestFree.y;
        item.columns = bestArr.columns;
        item.rows = bestArr.rows;
        item.width = bestArr.width;
        item.height = bestArr.height;
        occupyGroup(freeAreas, item);
    }

    spillOverflow(overflow, binRight, binBottom);
    return { didFit: overflow.length === 0, overflow };
}

export { pack, packGroups };
